/**
 * Snapshot row — single entry in the snapshots list
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { HelpCircle, History, RotateCcw, Trash2 } from 'lucide-react';
import type { VoluumFlowSnapshot } from '@trafficpulse/shared';

import { Button } from '@/components/ui/layout/Button';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

import { SnapshotActorBadge } from './attribution';
import {
  formatSnapshotDate,
  formatSnapshotSource,
  snapshotBadgeTone,
  snapshotSourceHelpText,
} from './format-snapshot';

export interface SnapshotRowProps {
  snapshot: VoluumFlowSnapshot;
  onRestore: (snap: VoluumFlowSnapshot) => void;
  onDelete: (snap: VoluumFlowSnapshot) => void;
  busy: boolean;
}

const metaDot = (
  <span className="select-none text-border" aria-hidden>
    ·
  </span>
);

const SnapshotRow: FC<SnapshotRowProps> = ({
  snapshot,
  onRestore,
  onDelete,
  busy,
}) => {
  const hasCreatedActor =
    Boolean(snapshot.createdByDisplayName) || Boolean(snapshot.createdByEmail);

  return (
    <li className="flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 shadow-sm">
      <div
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border',
          snapshotBadgeTone(snapshot.source),
        )}
        aria-hidden
      >
        <History className="h-3 w-3" />
      </div>
      <div className="min-w-0 flex-1 space-y-0">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-medium leading-tight text-foreground">
            {snapshot.label || formatSnapshotSource(snapshot.source)}
          </span>
          {snapshot.restoredAt && (
            <span className="shrink-0 rounded border border-warning/40 bg-warning/10 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-warning">
              Restored
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1 gap-y-0 text-[10px] leading-none text-muted-foreground">
          <span className="inline-flex shrink-0 items-center gap-0.5 tabular-nums">
            {formatSnapshotDate(snapshot.createdAt)}
            {metaDot}
            {formatSnapshotSource(snapshot.source)}
            <Tooltip
              content={snapshotSourceHelpText(snapshot.source)}
              side="top"
              interactive
              contentClassName="max-w-sm"
            >
              <HelpCircle
                className="h-2.5 w-2.5 shrink-0 text-muted-foreground"
                aria-label={`About ${formatSnapshotSource(snapshot.source)}`}
              />
            </Tooltip>
          </span>
          {hasCreatedActor && (
            <>
              {metaDot}
              <span className="inline-flex min-w-0 items-center gap-0.5">
                <span className="shrink-0">Saved</span>
                <SnapshotActorBadge
                  displayName={snapshot.createdByDisplayName}
                  email={snapshot.createdByEmail}
                  variant="muted"
                  compact
                />
              </span>
            </>
          )}
          {snapshot.restoredAt && (
            <>
              {metaDot}
              <span className="inline-flex min-w-0 items-center gap-0.5 text-warning">
                <span className="shrink-0 tabular-nums">
                  {formatSnapshotDate(snapshot.restoredAt)}
                </span>
                <SnapshotActorBadge
                  displayName={snapshot.restoredByDisplayName}
                  email={snapshot.restoredByEmail}
                  variant="warning"
                  compact
                />
              </span>
            </>
          )}
        </div>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={() => onRestore(snapshot)}
        className="h-7 shrink-0 px-2 text-xs"
      >
        <RotateCcw className="h-3 w-3" aria-hidden />
        <span className="ml-1 hidden sm:inline">Restore</span>
      </Button>
      {snapshot.source === 'manual' && (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          disabled={busy}
          onClick={() => onDelete(snapshot)}
          className="h-7 w-7 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
          aria-label="Delete snapshot"
        >
          <Trash2 className="h-3 w-3" aria-hidden />
        </Button>
      )}
    </li>
  );
};

export default SnapshotRow;
